import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { axiosInstance } from '../services/axios-instance';
import { errorMessage, parseAxiosError } from '../utils';
import { PaginateOptions, StateLoading } from '../services/types';
import { Kit, KitList } from '../services/kit-service/types';
import { KitService } from '../services/kit-service/service';

interface KitState {
  kits: Kit[];
  nextPage?: number;
  prevPage?: number;
  currentPage: number;
  totalPages: number;
  serverError: string;
  loadingKits: boolean;
  loadingResults: boolean;
  count: number;
  totalCount: number;
  deleteKitStatus: StateLoading<number>;
}

interface KitAction {
  listKits: { (options: PaginateOptions): Promise<KitList> };
  deleteKit: (params: { id: number }) => Promise<void>;

}

const initialState = {
  kits: [] as Kit[],
  nextPage: undefined,
  prevPage: undefined,
  loadingResults: false,
  currentPage: 1,
  totalPages: 1,
  count: 0,
  totalCount: 0,
  loadingKits: false,
  serverError: '',
  deleteKitStatus: {},
};

const kitService = new KitService(axiosInstance);

export const useKitStore = create<KitState & KitAction>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        deleteKit: async ({ id }) => {
          set({ deleteKitStatus: { isLoading: true } });
          try {
            await axiosInstance({
              method: 'DELETE',
              url: `/kits/${id}`,
            });
            set({
              deleteKitStatus: { isLoading: false },
            });
          } catch (err) {
            set({
              deleteKitStatus: { isLoading: false, error: errorMessage(err) },
            });
          }
        },
        listKits: async options => {
          set(() => ({ loadingKits: true }));
          let data: KitList;
          try {
            data = await kitService.list(options);
          } catch (error) {
            set(() => ({
              serverError: parseAxiosError(error),
              loadingKits: false,
            }));
            throw error;
          }
          set(() => ({
            kits: data.items.map((kit, index) => ({
              ...kit,
              id: index + 1,
              kitId: kit.id,
            })),
            nextPage: data.nextPage,
            prevPage: data.prevPage,
            currentPage: Number(data.page),
            totalPages: Number(data.totalPages),
            count: data.itemCount,
            totalCount: data.totalItems,
            loadingKits: false,
            serverError: '',
          }));
          return data;
        },
      }),
      { name: 'kit-storage' },
    ),
  ),
);
